import React from 'react';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  useAddComplainMutation,
  useEditComplainMutation
} from '../../../../redux/apis/ComplainApi';
import { useIsLoggedInQuery } from '../../../../redux/apis/authApi';

import { notification } from '../../../../utils/notification';
import { LB_HEADER } from '../../../../utils/constant/labels';
import { ROLES } from '../../../../utils/constant/global';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';
import { BT_BUTTON_LABELS } from '../../../../utils/constant/buttons';
import { complainRequestSchema } from '../../../../utils/schemas/complain';
import { getErrorMessage, matchData } from '../../../../utils/handler';
import { INPUT_LABEL, PLACEHOLDER } from '../../../../utils/constant/placeHolders';
import './ComplainFormModel.scss';

const { ADMIN, HR } = ROLES;
const { BTN_SAVE } = BT_BUTTON_LABELS;
const { LB_ADD_COMPLAIN_REQUEST } = LB_HEADER;
const { PH_COMPLAIN_TICKET_REASON } = PLACEHOLDER;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { LB_USER, LB_COMPLAIN_TICKET_REASON } = INPUT_LABEL;

const ComplainFormModel = ({ state, setState, onEmployeeDropDownRefetch }) => {
  const { data: isLogin } = useIsLoggedInQuery();
  const [addComplain] = useAddComplainMutation();
  const [editComplain] = useEditComplainMutation();

  const defaultValue = {
    userId: '',
    dateOfRequest: '',
    requestReason: ''
  };

  const {
    reset,
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(complainRequestSchema),
    values: state.isEditModalData?._id ? state.isEditModalData : defaultValue,
    defaultValues: defaultValue
  });

  const handleAddComplainRequest = async (complainRequest) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await addComplain(complainRequest);
      if (res.data?.success) {
        setState((prevState) => ({
          ...prevState,
          modalHeader: { title: '', isEdit: false },
          loading: false
        }));
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        reset(defaultValue);
      } else {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          modalHeader: { title: '', isEdit: false }
        }));
        notification(res?.error?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      setState((prevState) => ({ ...prevState, loading: false }));
      notification(error.message, TOAST_ERROR);
    }
  };

  const handleEditComplainRequest = async (complainRequest, id) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await editComplain({ data: complainRequest, id });
      if (res.data?.success) {
        setState((prevState) => ({
          ...prevState,
          modalHeader: { title: '', isEdit: false },
          isDeleteModal: { status: false, id: '' },
          isEditModal: { status: false, id: '' },
          isEditModalData: {},
          loading: false,
          responseReason: '',
          reason: ''
        }));
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        reset(defaultValue);
      } else {
        setState((prevState) => ({
          ...prevState,
          modalHeader: { title: '', isEdit: false },
          isDeleteModal: { status: false, id: '' },
          isEditModal: { status: false, id: '' },
          isEditModalData: {},
          loading: false,
          responseReason: '',
          reason: ''
        }));
        notification(res?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      setState((prevState) => ({ ...prevState, loading: false }));
      notification(error.message, TOAST_ERROR);
    }
  };

  const onSubmit = (data) => {
    const userId = data.userId || (isLogin && isLogin.data && isLogin.data._id);
    const complainRequest = {
      ...data,
      dateOfRequest: state?.isEditModalData?._id
        ? state?.isEditModalData.dateOfRequest
        : new Date().getTime(),
      userId: userId
    };

    if (state?.isEditModalData?._id) {
      const defaultValues = state?.isEditModalData;
      const updatedValues = matchData(complainRequest, defaultValues);
      handleEditComplainRequest(updatedValues, state?.isEditModalData?._id);
    } else {
      handleAddComplainRequest(complainRequest);
    }
  };

  const handleMenuScrollToBottom = async () => {
    if (state?.dropdownData?.isNext) {
      setState((prevState) => ({
        ...prevState,
        currentPageDropdown: prevState.currentPageDropdown + 1
      }));
      onEmployeeDropDownRefetch?.();
    }
  };

  const clearState = () => {
    reset(defaultValue);
    setState((prevState) => ({
      ...prevState,
      reason: '',
      reasonError: '',
      responseReason: '',
      responseReasonError: '',
      loading: false,
      modalHeader: {
        ...prevState.modalHeader,
        title: LB_ADD_COMPLAIN_REQUEST,
        isEdit: false
      },
      isEditModalData: {}
    }));
  };

  return (
    <Modal
      centered
      className="complain-request-modal"
      dialogClassName="complain-request-modal__dialog"
      contentClassName="complain-request-modal__content"
      show={state.modalHeader.isEdit}
      onHide={() => {
        clearState();
      }}
    >
      <Modal.Header className="complain-request-modal__header">
        <div className="complain-request-modal__header-content">
          <span className="complain-request-modal__header-icon" aria-hidden="true">
            <i className="icofont-speech-comments" />
          </span>
          <div className="complain-request-modal__header-copy">
            <Modal.Title className="fw-bold complain-request-modal__title">
              {state.modalHeader.title}
            </Modal.Title>
            <p className="complain-request-modal__subtitle">
              Describe the complaint so the team can follow up
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close complain-request-modal__close"
          aria-label="Close"
          onClick={clearState}
        />
      </Modal.Header>
      <Modal.Body className="complain-request-modal__body">
        <div className="deadline-form complain-request-modal__card">
          <form className="complain-request-modal__form" onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-sm-6">
                {[ADMIN, HR].includes(isLogin?.data?.hrxRole?.roleName) && (
                  <div className="mb-3 complain-request-modal__field">
                    <label className="form-label complain-request-modal__label">{LB_USER}</label>
                    <Select
                      className="complain-request-modal__select"
                      classNamePrefix="complain-request-select"
                      options={state.dropdownDataStore?.map((val) => ({
                        value: val._id,
                        label: val.name
                      }))}
                      value={
                        state.isEditModalData?.user?._id
                          ? {
                              value: state.isEditModalData.user._id,
                              label: state.isEditModalData.user.name
                            }
                          : undefined
                      }
                      onChange={(selectedOption) => {
                        setValue('userId', selectedOption?.value); // Update userId in the form
                      }}
                      onMenuScrollToBottom={handleMenuScrollToBottom}
                      placeholder="Select option..."
                      isDisabled={!!state.isEditModalData?.user?._id} // Disable if user is already selected
                    />

                    <small className="text-danger complain-request-modal__error">
                      {getErrorMessage('userId', errors)}
                    </small>
                  </div>
                )}
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="mb-1 complain-request-modal__field">
                <label
                  htmlFor="exampleFormControlTextarea78d"
                  className="form-label complain-request-modal__label"
                >
                  {LB_COMPLAIN_TICKET_REASON}
                </label>
                <textarea
                  className="form-control complain-request-modal__textarea"
                  placeholder={PH_COMPLAIN_TICKET_REASON}
                  {...register('requestReason')}
                  id="exampleFormControlTextarea78d"
                  rows="3"
                ></textarea>
                <small className="text-danger complain-request-modal__error">
                  {getErrorMessage('requestReason', errors)}
                </small>
                <p className="complain-request-modal__hint">
                  Include what happened and what you need resolved.
                </p>
              </div>
            </div>
            <Modal.Footer className="complain-request-modal__footer">
              <button
                type="submit"
                disabled={state.loading}
                className="btn complain-request-modal__submit"
              >
                {state.loading ? (
                  <div className="spinner-border spinner-border-sm" role="status"></div>
                ) : (
                  <>
                    <i className="icofont-check-circled" aria-hidden="true" />
                    {BTN_SAVE}
                  </>
                )}
              </button>
            </Modal.Footer>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ComplainFormModel;
